import {
  extractAxiosErrorMessage,
  PutSiteIngestionRequest,
  ReduxiControllerDevicesResponse,
  SiteIngestionConfig,
  SolaredgeApiMode,
} from "@inrange/building-manager-api-client";
import {
  getESysName,
  Site,
  SiteElectricalSystem,
} from "@inrange/building-manager-api-client/models-site";
import { Icons } from "@inrange/theme-components/icons";
import { useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { UseMutationResult } from "@tanstack/react-query";

interface DeviceEntry {
  serial_number: string;
  hardware_type: "inverter" | "epm" | "meter" | "battery";
  meter_type?: "grid" | "pv" | "battery";
  device_name?: string;
}

const recordToDevices = (
  record: Record<string, DeviceEntry> | undefined
): DeviceEntry[] => {
  if (!record) return [];
  return Object.values(record);
};

const devicesToRecord = (
  devices: DeviceEntry[]
): Record<string, DeviceEntry> => {
  const record: Record<string, DeviceEntry> = {};
  for (const device of devices) {
    const entry: DeviceEntry = {
      hardware_type: device.hardware_type,
      serial_number: device.serial_number,
    };
    if (device.meter_type) entry.meter_type = device.meter_type;
    if (device.device_name) entry.device_name = device.device_name;
    record[device.serial_number] = entry;
  }
  return record;
};

const SOLAREDGE_API_MODE_OPTIONS: { value: SolaredgeApiMode; label: string }[] =
  [
    { value: "v1", label: "V1" },
    { value: "v1+v2", label: "V1, with a V2 copy to compare" },
    { value: "v2+v1", label: "V2, with a V1 copy to compare" },
    { value: "v2", label: "V2" },
  ];

const SolaredgeApiModeSelect = ({
  value,
  onChange,
}: {
  value: SolaredgeApiMode;
  onChange: (mode: SolaredgeApiMode) => void;
}) => (
  <Form.Group>
    <Form.Label>SolarEdge API</Form.Label>
    <Form.Select
      value={value}
      onChange={(e) => onChange(e.target.value as SolaredgeApiMode)}
    >
      {SOLAREDGE_API_MODE_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Form.Select>
    <Form.Text className="text-muted">
      The first API&apos;s SolarEdge data (on Reduxi sites, only per-inverter
      telemetry) feeds billing, charts and health checks. The copy is written to
      a separate table for comparison only. V1 modes need the API key.
    </Form.Text>
  </Form.Group>
);

const DevicesEditor = ({
  devices,
  onChange,
  dataSourceType,
}: {
  devices: DeviceEntry[];
  onChange: (devices: DeviceEntry[]) => void;
  dataSourceType: "solis" | "reduxi";
}) => {
  const hardwareTypes: DeviceEntry["hardware_type"][] =
    dataSourceType === "solis"
      ? ["inverter", "epm"]
      : ["inverter", "meter", "battery"];

  const idLabel = dataSourceType === "solis" ? "Serial number" : "Device ID";

  const addDevice = () => {
    onChange([...devices, { serial_number: "", hardware_type: "inverter" }]);
  };

  const removeDevice = (index: number) => {
    onChange(devices.filter((_, i) => i !== index));
  };

  const updateDevice = (index: number, updates: Partial<DeviceEntry>) => {
    onChange(
      devices.map((d, i) => {
        if (i !== index) return d;
        const updated = { ...d, ...updates };
        if (updated.hardware_type !== "meter") {
          delete updated.meter_type;
        }
        return updated;
      })
    );
  };

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Form.Label className="mb-0 fw-semibold">Devices</Form.Label>
        <Button variant="outline-primary" size="sm" onClick={addDevice}>
          + Add device
        </Button>
      </div>
      {devices.length === 0 ? (
        <div className="text-muted small">No devices configured</div>
      ) : (
        devices.map((device, index) => (
          <div key={index} className="border rounded p-2 mb-2">
            {dataSourceType === "reduxi" && device.device_name && (
              <div className="text-muted small mb-1">{device.device_name}</div>
            )}
            <Row className="align-items-end g-2">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small mb-1">{idLabel}</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={device.serial_number}
                    onChange={(e) =>
                      updateDevice(index, { serial_number: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small mb-1">Type</Form.Label>
                  <Form.Select
                    size="sm"
                    value={device.hardware_type}
                    onChange={(e) =>
                      updateDevice(index, {
                        hardware_type: e.target
                          .value as DeviceEntry["hardware_type"],
                      })
                    }
                  >
                    {hardwareTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              {dataSourceType === "reduxi" &&
                device.hardware_type === "meter" && (
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small mb-1">Meter type</Form.Label>
                      <Form.Select
                        size="sm"
                        value={device.meter_type ?? "grid"}
                        onChange={(e) =>
                          updateDevice(index, {
                            meter_type: e.target.value as
                              | "grid"
                              | "pv"
                              | "battery",
                          })
                        }
                      >
                        <option value="grid">grid</option>
                        <option value="pv">pv</option>
                        <option value="battery">battery</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              <Col xs="auto">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => removeDevice(index)}
                  title="Remove device"
                >
                  &times;
                </Button>
              </Col>
            </Row>
          </div>
        ))
      )}
    </div>
  );
};

const DataIngestionConfigModal = ({
  site,
  selectedEsysUuid,
  existingConfig,
  onSave,
  onDelete,
  isSaving,
  isDeleting,
  onClose,
  discoverReduxiDevices,
}: {
  site: Site;
  selectedEsysUuid: string;
  existingConfig: SiteIngestionConfig | null;
  onSave: (config: PutSiteIngestionRequest) => Promise<void>;
  onDelete?: () => Promise<void>;
  isSaving: boolean;
  isDeleting: boolean;
  onClose: () => void;
  discoverReduxiDevices: UseMutationResult<
    ReduxiControllerDevicesResponse,
    unknown,
    string,
    unknown
  >;
}) => {
  const dataSourceType = site.measurementType as
    | "solis"
    | "solaredge"
    | "reduxi";

  const esysEntries = Object.entries(site.esys)
    .filter(([, e]) => !!e)
    .sort(
      ([, a], [, b]) =>
        (a as SiteElectricalSystem).idx - (b as SiteElectricalSystem).idx
    );

  const selectedEsys = Object.values(site.esys)
    .filter((e) => !!e)
    .find(
      (e) => (e as SiteElectricalSystem).uuid === selectedEsysUuid
    ) as SiteElectricalSystem;

  const displaySiteName =
    esysEntries.length > 1
      ? `${site.name} - ${getESysName(esysEntries.length, selectedEsys).trim()}`
      : site.name;

  // Solis fields
  const [solisSiteId, setSolisSiteId] = useState(
    existingConfig?.solis_site_id ?? ""
  );
  const [solisDevices, setSolisDevices] = useState<DeviceEntry[]>(
    recordToDevices(existingConfig?.solis_devices)
  );

  // Reduxi fields
  const [reduxiControllerId, setReduxiControllerId] = useState(
    existingConfig?.reduxi_controller_id ?? ""
  );
  const [reduxiDevices, setReduxiDevices] = useState<DeviceEntry[]>(
    recordToDevices(existingConfig?.reduxi_devices)
  );
  const [isolarcloudLink, setIsolarcloudLink] = useState(
    existingConfig?.isolarcloud_link ?? ""
  );

  // SolarEdge fields
  const [solaredgeApiKey, setSolaredgeApiKey] = useState(
    existingConfig?.solaredge_api_key ?? ""
  );
  const [solaredgeSiteId, setSolaredgeSiteId] = useState(
    existingConfig?.solaredge_site_id ?? ""
  );
  const solaredgeTimezone = existingConfig?.solaredge_timezone ?? site.timeZone;
  const [solaredgeApiMode, setSolaredgeApiMode] = useState<SolaredgeApiMode>(
    existingConfig?.solaredge_api_mode ?? "v1"
  );

  // Earliest raw data date - stored as UTC ISO string, displayed in site timezone
  // Display format: "YYYY-MM-DD HH:MM"
  const utcToLocal = (utcStr: string): string => {
    if (!utcStr) return "";
    const d = new Date(utcStr);
    if (isNaN(d.getTime())) return "";
    const local = new Date(
      d.toLocaleString("en-US", { timeZone: site.timeZone })
    );
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())} ${pad(local.getHours())}:${pad(local.getMinutes())}`;
  };

  const localToUtc = (localStr: string): string => {
    if (!localStr) return "";
    // Parse "YYYY-MM-DD HH:MM" and interpret in site timezone
    const isoStr = localStr.replace(" ", "T");
    const fakeUtc = new Date(isoStr + "Z");
    if (isNaN(fakeUtc.getTime())) return "";
    const utcRef = new Date(
      fakeUtc.toLocaleString("en-US", { timeZone: "UTC" })
    );
    const tzRef = new Date(
      fakeUtc.toLocaleString("en-US", { timeZone: site.timeZone })
    );
    const offsetMs = utcRef.getTime() - tzRef.getTime();
    const utc = new Date(fakeUtc.getTime() + offsetMs);
    return utc.toISOString();
  };

  const [earliestRawDataDateLocal, setEarliestRawDataDateLocal] = useState(
    utcToLocal(existingConfig?.earliest_raw_data_date ?? "")
  );

  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      const config: PutSiteIngestionRequest = {
        site_name: displaySiteName,
        esys_uuid: selectedEsysUuid,
        data_source_type: dataSourceType,
      };

      if (earliestRawDataDateLocal) {
        config.earliest_raw_data_date = localToUtc(earliestRawDataDateLocal);
      }

      if (dataSourceType === "solis") {
        if (solisSiteId) config.solis_site_id = solisSiteId;
        config.solis_devices = devicesToRecord(solisDevices);
      } else if (dataSourceType === "reduxi") {
        config.reduxi_controller_id = reduxiControllerId;
        config.reduxi_devices = devicesToRecord(reduxiDevices);
        if (solaredgeSiteId) {
          config.solaredge_site_id = solaredgeSiteId;
          // Every SolarEdge pull, V1 or V2, windows on the site's timezone.
          config.solaredge_timezone = solaredgeTimezone;
          config.solaredge_api_mode = solaredgeApiMode;
        }
        if (solaredgeApiKey) {
          config.solaredge_api_key = solaredgeApiKey;
          config.solaredge_timezone = solaredgeTimezone;
        }
        if (isolarcloudLink) config.isolarcloud_link = isolarcloudLink;
      } else if (dataSourceType === "solaredge") {
        config.solaredge_api_key = solaredgeApiKey;
        config.solaredge_site_id = solaredgeSiteId;
        config.solaredge_timezone = solaredgeTimezone;
        config.solaredge_api_mode = solaredgeApiMode;
      }

      await onSave(config);
    } catch (err) {
      setError(await extractAxiosErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    if (
      !window.confirm("Are you sure you want to delete this ingestion config?")
    ) {
      return;
    }
    setError(null);
    try {
      await onDelete();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to delete ingestion config";
      setError(errorMessage);
    }
  };

  const handleDiscoverDevices = async () => {
    if (!reduxiControllerId) return;
    setError(null);
    try {
      const result =
        await discoverReduxiDevices.mutateAsync(reduxiControllerId);
      const discoveredDevices: DeviceEntry[] = Object.values(
        result.reduxi_devices
      );
      setReduxiDevices(discoveredDevices);
      if (result.unmapped_devices.length > 0) {
        setError(
          `Discovered ${discoveredDevices.length} devices. ${result.unmapped_devices.length} unmapped device(s) were skipped: ${result.unmapped_devices.map((d) => `${d.name} (${d.deviceCategory})`).join(", ")}`
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to discover devices";
      setError(errorMessage);
    }
  };

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {existingConfig ? "Edit" : "Configure"} data ingestion config
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Site name</Form.Label>
                <Form.Control type="text" value={displaySiteName} disabled />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Data source type</Form.Label>
                <Form.Control type="text" value={dataSourceType} disabled />
              </Form.Group>
            </Col>
          </Row>

          {dataSourceType === "solis" && (
            <>
              <Row className="mb-3 align-items-end">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Solis site ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={solisSiteId}
                      onChange={(e) => setSolisSiteId(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end pb-2">
                  {solisSiteId && (
                    <a
                      href={`https://www.soliscloud.com/station/stationDetails/generalSituation/${solisSiteId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#0d6efd",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                    >
                      Solis monitoring portal
                      <img
                        alt="view"
                        src={Icons.shareBlue}
                        style={{
                          width: "17px",
                          height: "17px",
                          marginLeft: "8px",
                          marginBottom: "2px",
                        }}
                      />
                    </a>
                  )}
                </Col>
              </Row>
              <DevicesEditor
                devices={solisDevices}
                onChange={setSolisDevices}
                dataSourceType="solis"
              />
            </>
          )}

          {dataSourceType === "reduxi" && (
            <>
              <Row className="mb-3 align-items-end">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>SolarEdge site ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={solaredgeSiteId}
                      onChange={(e) => setSolaredgeSiteId(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>SolarEdge API key</Form.Label>
                    <Form.Control
                      type="text"
                      value={solaredgeApiKey}
                      onChange={(e) => setSolaredgeApiKey(e.target.value)}
                      placeholder="For per-inverter outage detection"
                    />
                    <Form.Text className="text-muted">
                      Optional. Meter and battery readings come via the Reduxi
                      gateway; this key is only used to pull per-inverter
                      SolarEdge telemetry for inverter outage detection (needs
                      2+ inverters).
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end pb-2">
                  {solaredgeSiteId && (
                    <a
                      href={`https://monitoring.solaredge.com/one#/commercial/power-flow?siteId=${solaredgeSiteId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#0d6efd",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                    >
                      SolarEdge monitoring portal
                      <img
                        alt="view"
                        src={Icons.shareBlue}
                        style={{
                          width: "17px",
                          height: "17px",
                          marginLeft: "8px",
                          marginBottom: "2px",
                        }}
                      />
                    </a>
                  )}
                </Col>
              </Row>
              {solaredgeSiteId && (
                <Row className="mb-3">
                  <Col md={8}>
                    <SolaredgeApiModeSelect
                      value={solaredgeApiMode}
                      onChange={setSolaredgeApiMode}
                    />
                  </Col>
                </Row>
              )}
              {!!site.totalBatterySummary && (
                <Row className="mb-3 align-items-end">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label>iSolarCloud link</Form.Label>
                      <Form.Control
                        type="text"
                        value={isolarcloudLink}
                        onChange={(e) => setIsolarcloudLink(e.target.value)}
                        placeholder="https://web3.isolarcloud.eu/#/plantDetail/overView?..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="d-flex align-items-end pb-2">
                    {isolarcloudLink && (
                      <a
                        href={isolarcloudLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#0d6efd",
                          textDecoration: "none",
                          cursor: "pointer",
                        }}
                      >
                        Sungrow monitoring portal
                        <img
                          alt="view"
                          src={Icons.shareBlue}
                          style={{
                            width: "17px",
                            height: "17px",
                            marginLeft: "8px",
                            marginBottom: "2px",
                          }}
                        />
                      </a>
                    )}
                  </Col>
                </Row>
              )}
              <Row className="mb-3 align-items-end">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Reduxi controller ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={reduxiControllerId}
                      onChange={(e) => setReduxiControllerId(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end pb-2">
                  <a
                    href="https://www.reduxi.app/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#0d6efd",
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                  >
                    Reduxi monitoring portal
                    <img
                      alt="view"
                      src={Icons.shareBlue}
                      style={{
                        width: "17px",
                        height: "17px",
                        marginLeft: "8px",
                        marginBottom: "2px",
                      }}
                    />
                  </a>
                </Col>
                <Col
                  md={4}
                  className="d-flex align-items-end justify-content-end pb-2"
                >
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleDiscoverDevices}
                    disabled={
                      !reduxiControllerId || discoverReduxiDevices.isLoading
                    }
                  >
                    {discoverReduxiDevices.isLoading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Discovering...
                      </>
                    ) : (
                      "Discover devices"
                    )}
                  </Button>
                </Col>
              </Row>
              <DevicesEditor
                devices={reduxiDevices}
                onChange={setReduxiDevices}
                dataSourceType="reduxi"
              />
            </>
          )}

          {dataSourceType === "solaredge" && (
            <>
              <Row className="mb-3 align-items-end">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>SolarEdge site ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={solaredgeSiteId}
                      onChange={(e) => setSolaredgeSiteId(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>SolarEdge API key</Form.Label>
                    <Form.Control
                      type="text"
                      value={solaredgeApiKey}
                      onChange={(e) => setSolaredgeApiKey(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end pb-2">
                  {solaredgeSiteId && (
                    <a
                      href={`https://monitoring.solaredge.com/one#/commercial/power-flow?siteId=${solaredgeSiteId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#0d6efd",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                    >
                      SolarEdge monitoring portal
                      <img
                        alt="view"
                        src={Icons.shareBlue}
                        style={{
                          width: "17px",
                          height: "17px",
                          marginLeft: "8px",
                          marginBottom: "2px",
                        }}
                      />
                    </a>
                  )}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={8}>
                  <SolaredgeApiModeSelect
                    value={solaredgeApiMode}
                    onChange={setSolaredgeApiMode}
                  />
                </Col>
              </Row>
            </>
          )}

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Earliest raw data (Optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={earliestRawDataDateLocal}
                  onChange={(e) => setEarliestRawDataDateLocal(e.target.value)}
                  placeholder="Example: 2026-03-04 16:00"
                />
                <Form.Text className="text-muted">
                  In {site.timeZone} time. Readings before this will be rejected
                  during ingestion.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {onDelete && (
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={isSaving || isDeleting}
            className="me-auto"
          >
            {isDeleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              "Delete config"
            )}
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isSaving || isDeleting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSaving || isDeleting}
        >
          {isSaving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DataIngestionConfigModal;
